// Tree-row concerns for `UI::TabulatorTableComponent`:
// - Native `<button>` HTML for Tabulator's expand/collapse toggle.
// - A focus watchdog, since Tabulator's re-render rips focus out.
// - `rowFormatter` for `<tr data-heading-row>` section labels.

import { t } from '../../javascript/lib/i18n';

// Focus-watchdog state. One document-level listener fronts every table; the
// most recent keyboard activation wins.
let pendingFocusRow = null;
let pendingFocusUntil = 0;
let watchdogInstalled = false;

// Tabulator clones these onto every parent row and wires up its own click
// handler, so a real `<button>` gives us Enter/Space, `:focus-visible`, and the
// `button` role for free.
//
// ARIA belongs in the markup, not stamped on after render: Tabulator re-clones
// the toggle on every row layout, and a forced redraw fires async from its own
// IntersectionObserver when an off-screen table scrolls into view — so anything
// applied afterwards is wiped, leaving no `aria-expanded` at all. It clones
// whichever prototype matches the row state, so in-markup attributes can't
// drift.
export function treeCollapseButtonHtml() {
  return (
    '<button type="button" class="tabulator-data-tree-control" ' +
    `aria-expanded="true" aria-label="${escapeAttribute(
      t('js.tabulator_table.tree.collapse_row')
    )}">` +
    '<span class="tabulator-data-tree-control-collapse"></span>' +
    '</button>'
  );
}
export function treeExpandButtonHtml() {
  return (
    '<button type="button" class="tabulator-data-tree-control" ' +
    `aria-expanded="false" aria-label="${escapeAttribute(
      t('js.tabulator_table.tree.expand_row')
    )}">` +
    '<span class="tabulator-data-tree-control-expand"></span>' +
    '</button>'
  );
}

function escapeAttribute(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Prime the watchdog on keyboard toggles so focus reseats onto the freshly
// cloned button. Capture phase because Tabulator's own click handler calls
// `stopPropagation()`. `detail === 0` means keyboard activation (pointer clicks
// carry a click count), so mouse users stay unprimed and don't pick up a stray
// focus outline.
export function installTreeKeyboardWatchdog(containerEl) {
  installTreeFocusWatchdog();
  containerEl.addEventListener(
    'click',
    (e) => {
      const button = e.target.closest?.('.tabulator-data-tree-control');
      if (!button || !containerEl.contains(button)) return;
      if (e.detail !== 0) return;
      pendingFocusRow = button.closest('.tabulator-row');
      // 400ms covers Tabulator's reinitialize + refreshData passes.
      pendingFocusUntil = Date.now() + 400;
    },
    true
  );
}

function installTreeFocusWatchdog() {
  if (watchdogInstalled) return;
  watchdogInstalled = true;
  document.addEventListener(
    'focusout',
    () => {
      if (!pendingFocusRow) return;
      if (Date.now() > pendingFocusUntil) {
        pendingFocusRow = null;
        return;
      }
      // focusout fires before activeElement updates, so defer a tick and
      // refocus only if focus actually landed on <body>.
      queueMicrotask(() => {
        if (!pendingFocusRow) return;
        if (document.activeElement !== document.body) return;
        const target = pendingFocusRow.querySelector(
          '.tabulator-data-tree-control'
        );
        if (target?.isConnected) target.focus();
      });
    },
    true
  );
}

// Render `<tr data-heading-row>` rows as a label spanning the children group.
// The first cell can't span it — Tabulator stamps an inline `width` on every
// cell — so hoist the heading into a row-level overlay instead; the cells stay
// put (hidden via SCSS) and the overlay spans freely.
export function treeRowFormatter(row) {
  applyRowDataAttrs(row);
  if (!row.getData()._heading) return;
  const el = row.getElement();
  if (el.dataset.headingFormatted) return;
  el.dataset.headingFormatted = 'true';
  el.classList.add('amba-tabulator-tree-heading');
  // First *visible* cell: a download-only hidden column (the CSV group column)
  // renders a blank leading cell, which would leave the heading empty.
  const headingCell = row
    .getCells()
    .find((cell) => cell.getColumn().isVisible());
  const overlay = document.createElement('div');
  overlay.className = 'amba-tabulator-tree-heading-overlay';
  overlay.innerHTML = headingCell ? headingCell.getElement().innerHTML : '';
  el.appendChild(overlay);
}

// Copy the source `<tr>`'s data attributes (captured by `extractRowData` as
// `_dataAttrs`) onto the rendered row, so specs and CSS can target rows by
// domain id (e.g. `data-outcome-id="42"`) rather than Tabulator's classes.
// Also re-surfaces the stable row id and group, which `extractRowData` consumes
// internally (as `_rowId` / `_rowGroup`) and therefore keeps out of `_dataAttrs`.
// Without them a rendered row cannot be addressed by the same identity the
// source row declares, which is what a reorder controller needs. Inert for the
// tables that don't opt into `data-row-id`.
function applyRowDataAttrs(row) {
  const data = row.getData();
  const el = row.getElement();
  if (el.dataset.rowAttrsApplied) return;
  el.dataset.rowAttrsApplied = 'true';
  if (data._rowId !== undefined) el.dataset.rowId = data._rowId;
  if (data._rowGroup !== undefined) el.dataset.rowGroup = data._rowGroup;
  Object.entries(data._dataAttrs ?? {}).forEach(([key, value]) => {
    el.dataset[key] = value;
  });
}
